/**
 * PathwayEditor view (PROD-591, extended in Sprint 4.3.1 / PROD-650
 * and Sprint 4.3.2 / PROD-651).
 *
 * Replaces the Phase 1 PathwayStudio placeholder. Renders the master
 * prompt v3.3 §8 three-pane layout:
 *   - Left: <PathwayPalette> listing the 3-tier hierarchy + 6 activity
 *     types per the Awell design ontology
 *     (`awell/studio/docs/getting-started/design-ontology.md`).
 *   - Center: <PathwayCanvas> (React Flow) with pan, zoom, minimap,
 *     fit-to-view. Toggleable to <PathwayListFallback> via the
 *     "Show as list" button per HC6 a11y.
 *   - Right: <PathwayInspector> in Design Mode, <ActivityInspector> in
 *     Runtime Mode (Sprint 4.3.2).
 *
 * Sprint 4.3.1 (PROD-650) adds the 3-tier drill-down breadcrumb above
 * the canvas: `Pathway > Track > Step > Action`.
 *
 * Sprint 4.3.2 (PROD-651): Mode toggle (Design / Runtime) + ActivityInspector
 * + multiplicity badges + replay timeline. D4 RESOLVED: a single CPS
 * app + Mode toggle (NOT a separate runtime service).
 *   - Top toolbar: <PathwayModeToggle> as a top-right pill.
 *   - Runtime Mode: clicking an Action node opens <ActivityInspector>
 *     instead of the design-time <PathwayInspector>. Multiplicity
 *     badges render on Action nodes when N > 1 FHIR Tasks instantiate
 *     the action def. <PatientPathwayTimeline> appears as a collapsible
 *     sub-panel below the canvas.
 *   - Design Mode: legacy behaviour. Inspector + canvas unchanged.
 *
 * The view is the single integration surface; selection state lives in
 * `usePathwayStore` so the canvas + list fallback share a source of
 * truth and tests can assert one or the other without re-wiring.
 *
 * HC1 two-mode operation: edits emit `pathway_edit` with the `mode`
 * field on every save (the store handles emission). The toolbar
 * surfaces the active runtime mode (Demo / Live) read-only via the
 * <ModeBadge>; the editor mode (Design / Runtime) is an independent
 * UI-side toggle persisted in localStorage `cps:pathway-editor:mode`.
 *
 * HC6 a11y: the canvas / list toggle button is keyboard-focusable; the
 * list view mirrors the canvas content row by row. The mode toggle is
 * `role="radiogroup"` per <PathwayModeToggle>.
 *
 * HC5: zero em / en dashes anywhere in this file.
 */
import { useEffect, useMemo, useState } from 'react';
import { ModeBadge } from '../components/ModeBadge';
import { ActivityInspector } from '../components/pathway/ActivityInspector';
import { CareFlowComponentLeftRail } from '../components/pathway/CareFlowComponentLeftRail';
import { ComponentFolderView } from '../components/pathway/ComponentFolderView';
import { ComponentJsonEditor } from '../components/pathway/ComponentJsonEditor';
import { NewPathwayDialog } from '../components/pathway/NewPathwayDialog';
import { PathwayCanvas } from '../components/pathway/PathwayCanvas';
import { PathwayCanvasToolbar } from '../components/pathway/PathwayCanvasToolbar';
import { PathwayInspector } from '../components/pathway/PathwayInspector';
import { PathwayListFallback } from '../components/pathway/PathwayListFallback';
import { PathwayModeBanner } from '../components/pathway/PathwayModeBanner';
import { PathwayModeToggle } from '../components/pathway/PathwayModeToggle';
import { PathwayPalette } from '../components/pathway/PathwayPalette';
import { PathwayPublishFlow } from '../components/pathway/PathwayPublishFlow';
import { PatientPathwayTimeline } from '../components/pathway/PatientPathwayTimeline';
import {
  ViewSwitcher,
  type PathwayEditorView,
} from '../components/pathway/ViewSwitcher';
import { evaluatePublishGate } from '../services/care-flow-components/publish-gate';
import {
  auditPayloadToFhir,
  type VerbAuditPayload,
} from '../services/fhir-mappers/audit-event';
import { SYNTHETIC_PATIENTS } from '../data/synthetic-patients';
import {
  CARE_FLOW_FOLDERS,
  type CareFlowFolder,
  type CareFlowComponentJson,
} from '../services/care-flow-components/types';
import { countComponentsByFolder } from '../services/care-flow-components/serializer';
import { useCareFlowComponentStore } from '../state/care-flow-components.store';
import {
  PATHWAY_IDS,
  usePathwayStore,
  type PathwayId,
} from '../state/pathway.store';
import type { HierarchyLevel } from '../services/pathway-layout';
import { useTenantStore } from '../state/tenant.store';
import { useModeStore } from '../state/mode.store';
import { usePathwayEditorModeStore } from '../state/pathway-editor-mode.store';
import { usePaletteArmingStore } from '../state/palette-arming.store';
import {
  bundleToPathwayActivities,
  filterActivitiesByActionDef,
} from '../services/pathway-activities';
import type { PathwayActivity } from '../types/pathway-activity';
import { getFhirClient, type FhirClient } from '../services/fhir';
import type { PathwayNodeType } from '../services/pathway-layout';

/**
 * Brand-Patch v2: PATHWAY_DISPLAY_NAMES. Zero `\bTLP[1-4]\b` user-facing
 * strings. Lookup is the canonical source of truth for the editor-side
 * pathway label so banner + toolbar + dialog text all share one map.
 */
const PATHWAY_LABELS: Record<PathwayId, string> = {
  'cardiometabolic-pathway': 'Cardiometabolic Pathway',
  'hrt-pathway': 'HRT Pathway',
  'metabolic-syndrome-pathway': 'Metabolic Syndrome Pathway',
  'cvd-prevention-pathway': 'CVD Prevention Pathway',
};
/** Sprint 4.3.7: alias matches the canonical lookup name in AppShell. */
export const PATHWAY_DISPLAY_NAMES = PATHWAY_LABELS;

const SHOW_AS_LIST_LABEL = 'Show as list';
const SHOW_AS_CANVAS_LABEL = 'Show as canvas';

/**
 * Sprint 4.3.7: localStorage key for the "Show advanced" toggle.
 * Default: hidden. The toggle gates Mode toggle, ActivityInspector,
 * multiplicity badges, timeline panel, and 16-folder JSON view tab.
 */
const SHOW_ADVANCED_STORAGE_KEY = 'cps:pathway-editor:show-advanced';

/**
 * Sprint 4.3.7: HierarchyLevel-aware activity-type → action category
 * map for drag-from-palette drops. Activity drops become Action nodes
 * with `category` resolved per the Awell ontology.
 */
function activityTypeToCategory(
  activityType?: PathwayNodeType,
): 'user' | 'system' | undefined {
  if (!activityType) return undefined;
  switch (activityType) {
    // User-facing activities: forms, manual review, NBA approval.
    case 'demographics':
    case 'vitals':
    case 'labs':
    case 'nba':
      return 'user';
    // System-driven activities: decision-point routing, outcome rollup.
    case 'decision':
    case 'outcome':
      return 'system';
    default:
      return undefined;
  }
}

export interface PathwayEditorProps {
  /**
   * Optional FHIR client override for Runtime-Mode Task search. Tests
   * inject a deterministic `DemoFhirClient` to drive the multiplicity
   * + timeline assertions without round-tripping the real factory.
   */
  fhirClient?: FhirClient;
  /**
   * Optional pre-resolved activity list for Runtime Mode. When present,
   * the editor skips the FHIR fetch and uses these activities directly.
   * Tests prefer this path so the assertions stay synchronous.
   */
  activitiesOverride?: PathwayActivity[];
}

export function PathwayEditor({
  fhirClient,
  activitiesOverride,
}: PathwayEditorProps = {}) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const mode = useModeStore((s) => s.mode);
  const editorMode = usePathwayEditorModeStore((s) => s.mode);
  const [activePathway, setActivePathway] = useState<PathwayId>(
    'cardiometabolic-pathway',
  );
  // Sprint 4.3.6: consolidated view-switcher (canvas / list / folders)
  // replaces the prior twin toggle buttons. Source-of-truth in a single
  // discriminated state.
  const [view, setView] = useState<PathwayEditorView>('canvas');
  const showAsList = view === 'list';
  const showComponentsView = view === 'folders';
  const [showTimeline, setShowTimeline] = useState(true);
  const [activities, setActivities] = useState<PathwayActivity[]>(
    activitiesOverride ?? [],
  );
  // Sprint 4.3.6: drag-from-palette onboarding hint. Persists per session
  // so the hint is dismissable + sticky across remounts. The hint only
  // surfaces in Design Mode + Canvas view (the only place dragging makes
  // sense).
  const DRAG_HINT_STORAGE_KEY = 'cps:pathway-editor:drag-hint-seen';
  const [showDragHint, setShowDragHint] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    try {
      return localStorage.getItem(DRAG_HINT_STORAGE_KEY) !== 'true';
    } catch {
      return false;
    }
  });
  // Sprint 4.3.4: 16-folder care-flow component view toggle.
  const [activeFolder, setActiveFolder] = useState<CareFlowFolder | null>(null);
  const [activeComponentId, setActiveComponentId] = useState<string | null>(
    null,
  );
  const [sharedWarningDismissedFor, setSharedWarningDismissedFor] = useState<
    string | null
  >(null);
  const hydrateFromPathway = useCareFlowComponentStore(
    (s) => s.hydrateFromPathway,
  );
  const setDraft = useCareFlowComponentStore((s) => s.setDraft);
  const commitDraft = useCareFlowComponentStore((s) => s.commitDraft);
  const careFlowState = useCareFlowComponentStore((s) => s.byPathwayKey);

  // Sprint 4.3.1: hierarchy drill-down state.
  const [hierarchyLevel, setHierarchyLevel] =
    useState<HierarchyLevel>('tracks');
  const [focusedTrackId, setFocusedTrackId] = useState<string | null>(null);
  const [focusedStepId, setFocusedStepId] = useState<string | null>(null);

  const getPathway = usePathwayStore((s) => s.getPathway);
  const selectNode = usePathwayStore((s) => s.selectNode);
  const selectedNodeByKey = usePathwayStore((s) => s.selectedNodeByKey);
  const addNode = usePathwayStore((s) => s.addNode);
  const addEdge = usePathwayStore((s) => s.addEdge);
  const createPathwayAction = usePathwayStore((s) => s.createPathway);
  const pathwaysByKey = usePathwayStore((s) => s.pathwaysByKey);

  // Sprint 4.3.7-fix: ESC disarms the click-to-add palette state. Wired
  // here (top of view) so the handler is alive whether the user is in
  // canvas or list / folders view.
  const disarmPalette = usePaletteArmingStore((s) => s.disarm);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        disarmPalette();
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
    return undefined;
  }, [disarmPalette]);

  // Sprint 4.3.7: "Show advanced" toggle. Default false (hidden) per
  // the founder direction: canvas + Pathway picker + ViewSwitcher +
  // Save&Publish are all that should be visible by default. Runtime
  // editor mode implies advanced (Mode toggle / ActivityInspector are
  // themselves advanced features) so the toggle effectively flips on
  // when `editorMode === 'runtime'`.
  const [showAdvancedRaw, setShowAdvanced] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    try {
      return localStorage.getItem(SHOW_ADVANCED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const showAdvanced = showAdvancedRaw || editorMode === 'runtime';
  const handleToggleAdvanced = () => {
    setShowAdvanced((prev) => {
      const next = !prev;
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem(SHOW_ADVANCED_STORAGE_KEY, String(next));
        } catch (err) {
          console.warn(
            '[pathway-editor] show-advanced persist failed; toggle will reset on refresh',
            err,
          );
        }
      }
      return next;
    });
  };

  // Sprint 4.3.7: "+ New Pathway" dialog state.
  const [showNewPathwayDialog, setShowNewPathwayDialog] = useState(false);

  // Sprint 4.3.7: Save & Publish toast.
  const [publishToast, setPublishToast] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);
  useEffect(() => {
    if (!publishToast) return;
    const t = setTimeout(() => setPublishToast(null), 4000);
    return () => clearTimeout(t);
  }, [publishToast]);

  // Sprint 4.3.7-fix: depend on `pathwaysByKey` so the memo invalidates
  // whenever the active pathway's snapshot changes. Previously the memo
  // only re-ran on tenant / activePathway change, so click-to-add +
  // edge-create + node-edit mutations were invisible to the canvas
  // until a tenant or pathway switch forced a re-resolve. The store
  // subscription `usePathwayStore((s) => s.pathwaysByKey)` triggers a
  // re-render on mutation; this memo now picks up the new entry.
  const pathway = useMemo(
    () => getPathway(tenantId, activePathway),
    [getPathway, tenantId, activePathway, pathwaysByKey],
  );

  /**
   * Sprint 4.3.7-fix: dynamic pathway picker entries. Source of truth is
   * the live store, filtered to the active tenant. The 4 canonical
   * pathway slugs always render even if not yet hydrated so the user
   * can switch to one without first triggering a getPathway hydrate.
   * User-created pathways override the canonical label via the
   * `pathway.name` field set by `createPathway`.
   *
   * Founder direction 2026-05-08 01:35: after creating "Demo Pathway",
   * the picker MUST surface "Demo Pathway" so the user can navigate
   * back to it. The previous hardcoded `PATHWAY_IDS.map` only ever
   * showed the four canonical labels.
   */
  const pickerEntries = useMemo(() => {
    const entries: Array<{ pathwayId: PathwayId; label: string }> = [];
    const seen = new Set<PathwayId>();
    // 1. Live store entries (filtered to current tenant). User-created
    //    pathways surface here with their typed `name`.
    for (const [key, p] of Object.entries(pathwaysByKey)) {
      if (!key.startsWith(`${tenantId}::`)) continue;
      const id = p.pathwayId;
      if (seen.has(id)) continue;
      seen.add(id);
      entries.push({
        pathwayId: id,
        label: p.name && p.name.trim().length > 0
          ? p.name
          : PATHWAY_LABELS[id] ?? id,
      });
    }
    // 2. Canonical 4 slugs always render so the user can pick a fixture
    //    that has not been hydrated yet. Order is the canonical list
    //    order so the picker reads the same on first paint.
    for (const id of PATHWAY_IDS) {
      if (seen.has(id)) continue;
      seen.add(id);
      entries.push({ pathwayId: id, label: PATHWAY_LABELS[id] });
    }
    return entries;
  }, [pathwaysByKey, tenantId]);
  const selectedNodeId =
    selectedNodeByKey[`${tenantId}::${activePathway}`] ?? null;

  const hasHierarchy = useMemo(
    () =>
      pathway.nodes.some(
        (n) =>
          n.nodeType === 'track' ||
          n.nodeType === 'step' ||
          n.nodeType === 'action',
      ),
    [pathway],
  );
  const focusedTrack = useMemo(
    () =>
      focusedTrackId === null
        ? null
        : pathway.nodes.find((n) => n.id === focusedTrackId) ?? null,
    [pathway, focusedTrackId],
  );
  const focusedStep = useMemo(
    () =>
      focusedStepId === null
        ? null
        : pathway.nodes.find((n) => n.id === focusedStepId) ?? null,
    [pathway, focusedStepId],
  );
  const focusedAction = useMemo(
    () =>
      selectedNodeId === null
        ? null
        : pathway.nodes.find(
            (n) => n.id === selectedNodeId && n.nodeType === 'action',
          ) ?? null,
    [pathway, selectedNodeId],
  );
  const focusedParentId =
    hierarchyLevel === 'tracks'
      ? null
      : hierarchyLevel === 'steps'
        ? focusedTrackId
        : focusedStepId;

  useEffect(() => {
    if (selectedNodeId !== null) {
      const exists = pathway.nodes.some((n) => n.id === selectedNodeId);
      if (!exists) {
        selectNode(tenantId, activePathway, null);
      }
    }
  }, [pathway, selectedNodeId, tenantId, activePathway, selectNode]);

  useEffect(() => {
    setHierarchyLevel('tracks');
    setFocusedTrackId(null);
    setFocusedStepId(null);
  }, [activePathway, tenantId]);

  // Runtime Mode: fetch FHIR Tasks for the active tenant and convert to
  // PathwayActivity[]. Skipped when tests pass `activitiesOverride`.
  useEffect(() => {
    if (activitiesOverride !== undefined) {
      setActivities(activitiesOverride);
      return;
    }
    if (editorMode !== 'runtime') {
      return;
    }
    let cancelled = false;
    const client = fhirClient ?? getFhirClient();
    client
      .searchTasks(tenantId)
      .then((bundle) => {
        if (cancelled) return;
        setActivities(bundleToPathwayActivities(bundle));
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn(
          '[pathway-editor] Task search failed; ActivityInspector will show empty state',
          err,
        );
        setActivities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [editorMode, tenantId, fhirClient, activitiesOverride]);

  // Build the multiplicity-count map keyed by Action node id. The Action
  // node id is the canonical action_def_id we filter against.
  const multiplicityCountsByActionId = useMemo(() => {
    if (editorMode !== 'runtime') return {};
    const counts: Record<string, number> = {};
    for (const node of pathway.nodes) {
      if (node.nodeType !== 'action') continue;
      counts[node.id] = filterActivitiesByActionDef(activities, node.id).length;
    }
    return counts;
  }, [pathway.nodes, activities, editorMode]);

  const handleSelectNode = (nodeId: string | null) => {
    if (nodeId !== null && hasHierarchy) {
      const node = pathway.nodes.find((n) => n.id === nodeId);
      if (node) {
        if (node.nodeType === 'track' && hierarchyLevel === 'tracks') {
          setFocusedTrackId(nodeId);
          setHierarchyLevel('steps');
        } else if (node.nodeType === 'step' && hierarchyLevel === 'steps') {
          setFocusedStepId(nodeId);
          setHierarchyLevel('actions');
        }
      }
    }
    selectNode(tenantId, activePathway, nodeId);
  };

  const handleBreadcrumbNavigate = (
    level: HierarchyLevel | 'pathway',
    _nodeId: string | null,
  ) => {
    if (level === 'pathway' || level === 'tracks') {
      setHierarchyLevel('tracks');
      setFocusedTrackId(null);
      setFocusedStepId(null);
      selectNode(tenantId, activePathway, null);
      return;
    }
    if (level === 'steps') {
      setHierarchyLevel('steps');
      setFocusedStepId(null);
      selectNode(tenantId, activePathway, null);
      return;
    }
  };

  // Decide which inspector to render. Runtime Mode opens ActivityInspector
  // ONLY when an Action is selected. For non-action nodes (track / step /
  // legacy activity types) the design-time inspector still applies because
  // those node types do not have a runtime activity counterpart.
  const showActivityInspector =
    editorMode === 'runtime' && focusedAction !== null;

  const focusedActionActivities = useMemo(
    () =>
      focusedAction === null
        ? []
        : filterActivitiesByActionDef(activities, focusedAction.id),
    [focusedAction, activities],
  );

  // Sprint 4.3.4: hydrate the care-flow bundle whenever the active
  // pathway changes. The bundle is a deterministic projection of the
  // pathway snapshot; the hook keeps it in sync.
  const pathwayKey = `${tenantId}::${activePathway}`;
  useEffect(() => {
    hydrateFromPathway(pathwayKey, pathway);
  }, [pathwayKey, pathway, hydrateFromPathway]);
  const careFlowEntry = careFlowState[pathwayKey];
  const careFlowBundle = careFlowEntry?.bundle;
  const careFlowDrafts = careFlowEntry?.drafts ?? {};
  const careFlowCounts = useMemo(
    () =>
      careFlowBundle
        ? countComponentsByFolder(careFlowBundle)
        : (Object.fromEntries(CARE_FLOW_FOLDERS.map((f) => [f, 0])) as Record<
            CareFlowFolder,
            number
          >),
    [careFlowBundle],
  );
  const activeComponent: CareFlowComponentJson | null = useMemo(() => {
    if (!careFlowBundle || !activeFolder || !activeComponentId) return null;
    const folderArr = careFlowBundle[activeFolder] as ReadonlyArray<CareFlowComponentJson>;
    return folderArr.find((c) => c.id === activeComponentId) ?? null;
  }, [careFlowBundle, activeFolder, activeComponentId]);
  const citedComponentIds = useMemo(() => {
    if (!careFlowBundle) return [];
    const ids = new Set<string>();
    for (const t of careFlowBundle.tracks) {
      for (const sid of t.stepIds) ids.add(sid);
    }
    for (const s of careFlowBundle.steps) {
      ids.add(s.trackId);
      for (const aid of s.actionIds) ids.add(aid);
    }
    for (const a of careFlowBundle.actions) {
      if (a.stepId) ids.add(a.stepId);
    }
    return [...ids];
  }, [careFlowBundle]);

  // Sprint 4.3.6: resolve the synthetic patient enrolled on the active
  // pathway. Drives the Runtime banner heading + the empty-state guard.
  // The synthetic-patients fixture set carries a 1:1 map from pathway id
  // to patient (Khalid: cardiometabolic, Fatima: hrt, Omar: metabolic-
  // syndrome, Saeed: cvd-prevention) so first-match is sufficient.
  const enrolledPatient = useMemo(
    () =>
      SYNTHETIC_PATIENTS.find((p) => p.pathway === activePathway) ?? null,
    [activePathway],
  );
  const patientName = enrolledPatient?.name ?? null;

  // Runtime + canvas + zero matched FHIR Tasks for any action def in the
  // active pathway = "no enrolled patients" empty state. The activities
  // array is the deterministic source-of-truth (already filtered to
  // tenant scope by `searchTasks`). Empty-state ONLY applies in canvas
  // view; list + folders views have their own affordances.
  const runtimeNoEnrolledPatient =
    editorMode === 'runtime' &&
    view === 'canvas' &&
    activities.length === 0;

  const handleSwitchToDesignMode = () => {
    usePathwayEditorModeStore.getState().setMode('design');
  };

  const handleDismissDragHint = () => {
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(DRAG_HINT_STORAGE_KEY, 'true');
      } catch (err) {
        console.warn(
          '[pathway-editor] drag-hint persist failed; will re-show next mount',
          err,
        );
      }
    }
    setShowDragHint(false);
  };

  // Auto-dismiss the drag hint after 8s in Design + Canvas. Effect runs
  // once per mount of the visible hint.
  useEffect(() => {
    const visible =
      showDragHint && editorMode === 'design' && view === 'canvas';
    if (!visible) return;
    const timer = setTimeout(handleDismissDragHint, 8000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDragHint, editorMode, view]);

  // Sprint 4.3.7: drop-from-palette handler. Resolves a sensible parent
  // node id from the active hierarchy level so the dropped node lands
  // inside the focused container.
  const handleAddNode = (init: {
    nodeType: PathwayNodeType;
    activityType?: PathwayNodeType;
    position: { x: number; y: number };
  }) => {
    const isHierarchy = ['track', 'step', 'action'].includes(init.nodeType);
    let parent: string | undefined;
    if (init.nodeType === 'step') {
      parent = focusedTrackId ?? undefined;
    } else if (init.nodeType === 'action') {
      parent = focusedStepId ?? focusedTrackId ?? undefined;
    }
    const category = isHierarchy
      ? init.nodeType === 'action'
        ? activityTypeToCategory(init.activityType)
        : undefined
      : undefined;
    const result = addNode(tenantId, activePathway, {
      nodeType: init.nodeType,
      parentNodeId: parent,
      position: init.position,
      category,
    });
    if (result === null) {
      console.warn('[pathway-editor] addNode failed for', init.nodeType);
    }
  };

  // Sprint 4.3.7: edge-creation handler.
  const handleAddEdge = (connection: {
    source: string | null;
    target: string | null;
  }) => {
    if (!connection.source || !connection.target) return;
    addEdge(tenantId, activePathway, {
      source: connection.source,
      target: connection.target,
    });
  };

  // Sprint 4.3.7: "+ New Pathway" submit. Routes through the store
  // action which seeds an Awell-style starter graph + emits the
  // pathway-create AuditEvent.
  const existingPathwayIds = useMemo(() => {
    const ids: PathwayId[] = [];
    for (const id of PATHWAY_IDS) {
      const key = `${tenantId}::${id}`;
      if (pathwaysByKey[key]) ids.push(id);
    }
    return ids;
  }, [pathwaysByKey, tenantId]);

  const handleCreatePathway = (args: {
    pathwayId: PathwayId;
    name: string;
    description: string;
  }) => {
    createPathwayAction(tenantId, args.pathwayId, {
      name: args.name,
      description: args.description,
    });
    setActivePathway(args.pathwayId);
    setShowNewPathwayDialog(false);
    setPublishToast({
      kind: 'success',
      message: `Created ${args.name}.`,
    });
  };

  // Sprint 4.3.7: Save & Publish gate evaluation. Mirrors the
  // PathwayPublishFlow gate so the toolbar tooltip + the inline panel
  // surface the same reasons.
  const publishGate = useMemo(() => {
    if (!careFlowBundle) {
      return { ok: false, reasons: ['no-bundle'], details: 'Bundle missing.' };
    }
    return evaluatePublishGate({
      openDraftCount: Object.keys(careFlowDrafts).length,
      saraPassCount: 28,
      saraTotalCount: 28,
      bundle: careFlowBundle,
      citedComponentIds,
    });
  }, [careFlowBundle, careFlowDrafts, citedComponentIds]);

  const handleSaveAndPublishFromCanvas = () => {
    if (!publishGate.ok || !careFlowBundle) {
      const details =
        'details' in publishGate ? (publishGate.details as string) : '';
      setPublishToast({
        kind: 'error',
        message: `Publish blocked. ${details}`,
      });
      return;
    }
    const payload: VerbAuditPayload = {
      recorded: new Date().toISOString(),
      source: 'pathway-publish-from-canvas',
      tenantId,
      subject: {
        reference: 'Practitioner/pathway-author',
        display: 'Pathway author',
      },
      verb: 'published',
      verbDisplay: 'Published from canvas',
      object: {
        reference: `urn:cps:pathway:${activePathway}`,
        display: PATHWAY_LABELS[activePathway],
        typeCode: 'pathway-version',
      },
      action: 'C',
      outcome: '0',
    };
    const event = auditPayloadToFhir(payload);
    const client = fhirClient ?? getFhirClient();
    void client.createAuditEvent(event, tenantId).catch((err) => {
      console.warn(
        '[pathway-editor] save-and-publish AuditEvent emit failed',
        err,
      );
    });
    setPublishToast({
      kind: 'success',
      message: `Published v0.5 to ${PATHWAY_LABELS[activePathway]}.`,
    });
  };

  return (
    <section
      aria-labelledby="view-title-pathway-editor"
      data-testid="pathway-editor"
      data-mode={mode}
      data-editor-mode={editorMode}
      data-tenant-id={tenantId}
      data-pathway={activePathway}
      data-hierarchy-level={hasHierarchy ? hierarchyLevel : 'legacy'}
      className="flex flex-col"
    >
      <header
        data-testid="pathway-editor-header"
        className="flex items-center justify-between gap-gutter-md border-b border-surface-border bg-surface-muted px-gutter-lg py-gutter-sm"
      >
        <div className="flex items-center gap-gutter-md">
          <h2
            id="view-title-pathway-editor"
            className="text-signal-xl font-semibold text-text-primary"
          >
            Pathway Editor
          </h2>
          <label
            htmlFor="pathway-editor-pathway-picker"
            className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
          >
            Pathway
          </label>
          <select
            id="pathway-editor-pathway-picker"
            data-testid="pathway-editor-pathway-picker"
            aria-label="Pathway"
            value={activePathway}
            onChange={(e) => setActivePathway(e.target.value as PathwayId)}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            {/*
              Sprint 4.3.7-fix: dynamic pathway picker. Reads the live
              store snapshot (filtered to the active tenant) so user-
              created pathways - which carry a `name` field set by the
              New Pathway dialog - surface in the dropdown by their real
              name. Falls back to the canonical PATHWAY_LABELS for the
              4 seeded pathway slugs that have not been overwritten yet.
              Founder direction 2026-05-08 01:35.
            */}
            {pickerEntries.map((entry) => (
              <option key={entry.pathwayId} value={entry.pathwayId}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-gutter-md">
          <ViewSwitcher
            value={
              !showAdvanced && view === 'folders' ? 'canvas' : view
            }
            onChange={(next) => {
              // 16-folder JSON view only available with advanced toggle on.
              if (next === 'folders' && !showAdvanced) return;
              setView(next);
              if (next !== 'folders') {
                setActiveFolder(null);
                setActiveComponentId(null);
              }
            }}
          />
          <button
            type="button"
            data-testid="pathway-editor-show-advanced-toggle"
            aria-pressed={showAdvancedRaw}
            onClick={handleToggleAdvanced}
            className={[
              'rounded-signal-pill border px-gutter-md py-1 text-signal-xs font-semibold uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
              showAdvancedRaw
                ? 'border-signal-primary-500 bg-signal-primary-100 text-signal-primary-700'
                : 'border-surface-border bg-surface-base text-text-secondary hover:text-text-primary',
            ].join(' ')}
          >
            {showAdvancedRaw ? 'Hide advanced' : 'Show advanced'}
          </button>
          {showAdvanced ? (
            <>
              <span data-testid="pathway-editor-mode-badge">
                <ModeBadge />
              </span>
              <PathwayModeToggle />
            </>
          ) : null}
        </div>
      </header>
      <PathwayCanvasToolbar
        breadcrumb={
          hasHierarchy
            ? {
                pathwayLabel:
                  pathway.name && pathway.name.trim().length > 0
                    ? pathway.name
                    : PATHWAY_LABELS[activePathway],
                trackLabel: focusedTrack?.label ?? null,
                stepLabel: focusedStep?.label ?? null,
                actionLabel: focusedAction?.label ?? null,
                trackId: focusedTrackId,
                stepId: focusedStepId,
                actionId: focusedAction?.id ?? null,
                onNavigate: handleBreadcrumbNavigate,
              }
            : null
        }
        onCreatePathway={() => setShowNewPathwayDialog(true)}
        onPlay={handleSaveAndPublishFromCanvas}
        publishDisabled={!publishGate.ok}
        publishDisabledReason={
          !publishGate.ok && 'details' in publishGate
            ? (publishGate.details as string)
            : undefined
        }
        publishVersionLabel="v0.5"
      />
      {view === 'canvas' && showAdvanced ? (
        <PathwayModeBanner
          mode={editorMode}
          clinicalPathwayName={
            pathway.name && pathway.name.trim().length > 0
              ? pathway.name
              : PATHWAY_LABELS[activePathway]
          }
          patientName={patientName}
        />
      ) : null}
      {publishToast ? (
        <div
          role={publishToast.kind === 'error' ? 'alert' : 'status'}
          data-testid="pathway-editor-publish-toast"
          data-toast-kind={publishToast.kind}
          className={[
            'flex items-center justify-between gap-gutter-md border-b px-gutter-md py-1 text-signal-sm',
            publishToast.kind === 'error'
              ? 'border-signal-danger-500 bg-surface-base text-signal-danger-500'
              : 'border-signal-success-500 bg-surface-base text-signal-success-500',
          ].join(' ')}
        >
          <span>{publishToast.message}</span>
          <button
            type="button"
            data-testid="pathway-editor-publish-toast-dismiss"
            aria-label="Dismiss notification"
            onClick={() => setPublishToast(null)}
            className="text-signal-xs font-semibold uppercase tracking-wide"
          >
            Dismiss
          </button>
        </div>
      ) : null}
      <NewPathwayDialog
        open={showNewPathwayDialog}
        pathwayIds={PATHWAY_IDS}
        existingPathwayIds={existingPathwayIds}
        onClose={() => setShowNewPathwayDialog(false)}
        onCreate={handleCreatePathway}
      />
      <div className="flex flex-1">
        {showComponentsView ? (
          <CareFlowComponentLeftRail
            activeFolder={activeFolder}
            countsByFolder={careFlowCounts}
            onSelectFolder={(folder) => {
              setActiveFolder(folder);
              setActiveComponentId(null);
              setSharedWarningDismissedFor(null);
            }}
          />
        ) : (
          <PathwayPalette />
        )}
        <div className="flex flex-1 flex-col">
          <div className="flex-1">
            {showComponentsView ? (
              activeFolder === null ? (
                <p
                  data-testid="care-flow-components-empty-state"
                  className="m-gutter-lg rounded-signal-md border border-dashed border-surface-border bg-surface-base p-gutter-lg text-center text-signal-sm text-text-tertiary"
                >
                  Select a folder from the left to view its components.
                </p>
              ) : activeComponent !== null ? (
                <ComponentJsonEditor
                  folder={activeFolder}
                  component={activeComponent}
                  blockOnSharedConfirm={
                    sharedWarningDismissedFor !== activeComponent.id
                  }
                  onAcceptSharedWarning={() =>
                    setSharedWarningDismissedFor(activeComponent.id)
                  }
                  onSave={(candidate) => {
                    setDraft(pathwayKey, {
                      folder: activeFolder,
                      componentId: activeComponent.id,
                      candidate: candidate as Record<string, unknown>,
                    });
                    commitDraft(
                      pathwayKey,
                      activeFolder,
                      activeComponent.id,
                    );
                  }}
                  onCancel={() => setActiveComponentId(null)}
                />
              ) : careFlowBundle ? (
                <ComponentFolderView
                  folder={activeFolder}
                  components={
                    careFlowBundle[activeFolder] as ReadonlyArray<CareFlowComponentJson>
                  }
                  selectedComponentId={activeComponentId}
                  onSelectComponent={(id) => setActiveComponentId(id)}
                />
              ) : null
            ) : showAsList ? (
              <PathwayListFallback
                pathway={pathway}
                selectedNodeId={selectedNodeId}
                onSelectNode={(nodeId) =>
                  selectNode(tenantId, activePathway, nodeId)
                }
              />
            ) : (
              <div className="relative h-full">
                <PathwayCanvas
                  pathway={pathway}
                  selectedNodeId={selectedNodeId}
                  onSelectNode={handleSelectNode}
                  hierarchyLevel={hasHierarchy ? hierarchyLevel : undefined}
                  focusedParentId={focusedParentId}
                  multiplicityCountsByActionId={
                    showAdvanced ? multiplicityCountsByActionId : {}
                  }
                  onAddNode={handleAddNode}
                  onConnect={handleAddEdge}
                />
                {runtimeNoEnrolledPatient ? (
                  <div
                    role="status"
                    aria-live="polite"
                    data-testid="pathway-editor-runtime-empty-state"
                    className="pointer-events-auto absolute inset-0 z-10 flex items-center justify-center bg-surface-muted/80 p-gutter-xl"
                  >
                    <div className="max-w-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-elevated">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        role="img"
                        aria-hidden="true"
                        className="mx-auto h-8 w-8 text-text-tertiary"
                      >
                        <circle
                          cx="9"
                          cy="8"
                          r="3.4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                        <path
                          d="M3 19c0-3 2.7-5 6-5s6 2 6 5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                        <circle
                          cx="17"
                          cy="9"
                          r="2.6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                      </svg>
                      <h3
                        data-testid="pathway-editor-runtime-empty-state-heading"
                        className="mt-gutter-sm text-center text-signal-base font-semibold text-text-primary"
                      >
                        No enrolled patients on this pathway
                      </h3>
                      <p className="mt-1 text-center text-signal-sm text-text-secondary">
                        Switch to Design Mode to author the template, or
                        enroll a patient via the Patient Picker.
                      </p>
                      <div className="mt-gutter-md flex justify-center">
                        <button
                          type="button"
                          data-testid="pathway-editor-runtime-empty-state-cta"
                          aria-label="Switch to Design Mode"
                          onClick={handleSwitchToDesignMode}
                          className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse hover:bg-signal-primary-600 focus:outline-none focus:ring-2 focus:ring-signal-primary-300"
                        >
                          Switch to Design Mode
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
                {showDragHint && editorMode === 'design' ? (
                  <div
                    role="status"
                    aria-live="polite"
                    data-testid="pathway-editor-drag-hint"
                    className="absolute bottom-gutter-md left-gutter-md z-10 flex max-w-xs items-start gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm shadow-signal-elevated"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-signal-sm bg-signal-primary-100 text-signal-primary-700"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        className="h-3.5 w-3.5"
                      >
                        <path
                          d="M6 12h8M10 8l4 4-4 4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <p className="flex-1 text-signal-xs text-text-primary">
                      Drag any item from the palette to add a node to your
                      pathway.
                    </p>
                    <button
                      type="button"
                      data-testid="pathway-editor-drag-hint-dismiss"
                      aria-label="Dismiss drag hint"
                      onClick={handleDismissDragHint}
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-signal-sm text-text-tertiary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          d="M6 6l12 12M18 6L6 18"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          {showComponentsView && careFlowBundle ? (
            <PathwayPublishFlow
              pathwayId={activePathway}
              tenantId={tenantId}
              pathwayLabel={PATHWAY_LABELS[activePathway]}
              bundle={careFlowBundle}
              openDraftCount={Object.keys(careFlowDrafts).length}
              saraPassCount={28}
              saraTotalCount={28}
              citedComponentIds={citedComponentIds}
              subjectReference="Practitioner/pathway-author"
            />
          ) : null}
          {!showComponentsView && editorMode === 'runtime' && showAdvanced ? (
            <div
              data-testid="pathway-editor-timeline-panel"
              className="border-t border-surface-border"
            >
              <button
                type="button"
                data-testid="pathway-editor-timeline-toggle"
                onClick={() => setShowTimeline((v) => !v)}
                aria-expanded={showTimeline}
                className="w-full rounded-none border-b border-surface-border bg-surface-muted px-gutter-md py-1 text-left text-signal-xs font-semibold uppercase tracking-wide text-text-secondary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
              >
                {showTimeline ? 'Hide timeline' : 'Show timeline'}
              </button>
              {showTimeline ? (
                <PatientPathwayTimeline activities={activities} />
              ) : null}
            </div>
          ) : null}
        </div>
        {!showComponentsView &&
        showActivityInspector &&
        focusedAction !== null &&
        showAdvanced ? (
          <ActivityInspector
            actionId={focusedAction.id}
            actionLabel={focusedAction.label}
            activities={focusedActionActivities}
          />
        ) : !showComponentsView ? (
          <PathwayInspector
            pathway={pathway}
            pathwayId={activePathway}
            selectedNodeId={selectedNodeId}
            fhirClient={fhirClient}
          />
        ) : null}
      </div>
    </section>
  );
}

export { PATHWAY_LABELS, SHOW_AS_LIST_LABEL, SHOW_AS_CANVAS_LABEL };
